<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { computed, nextTick, ref, useSlots, watch } from 'vue'
import { twMerge } from 'tailwind-merge'
import { useAuth } from '@/composables/auth'
import { useChallengeBase } from '@/composables/game/challenges'
import { type CardChallengeLeaderboardPlayer } from '@/types/leaderboard-player'
import { formatNumberFullTruncate, formatNumberSemiTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import ChallengeBoardCard2 from '@/components/ChallengeBoardCard2.vue'
import ChallengePrizeItem from '@/components/icons/ChallengePrizeItem.vue'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { type Challenge, isPrizePoolNft } from '@/types/shared/player-config'
import ClockIcon from '@/assets/images/gold-clock.png'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import ArrowRight from '@/components/icons/ArrowRight.vue'
import SummaryCard from '@/components/SummaryCard.vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { usePlayer } from '@/composables/game/player'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import { formatMillisecondDuration } from '@/helpers/date'

const props = defineProps<{
  show: boolean
  challenge: Challenge
  formattedCountdown: string
  onCtaClick?: () => void
  getLeaderboard: () => Promise<void>
  players: CardChallengeLeaderboardPlayer[],
  currentPlayer: { rank: number, score: number } | null
  backgroundColor?: string
  backgroundImage?: string,
  mainImage: string,
  mainImageClass?: string,
  mainImageBackgroundClass?: string,
  withStars?: boolean,
  timerBackgroundClass?: string,
  backgroundStyle?: Record<string, string>,
  timerBackgroundInlineStyle?: Record<string, string>,
  title?: string,
  description?: string,
  isOneTimeChallenge?: boolean
  ctaText?: string,
  ctaChallengeDoneText?: string,
  ruleCardLine1?: string,
  ruleCardLine2?: string,
  ruleCardImage?: string,
  scoreImage?: string,
  isScoreInMilliseconds?: boolean,
  isRuleCardClickable?: boolean,
  currencyLabel?: string ,
  currencyImage?: string,
  challengeCurrency?: string,
  isReturningFromGame?: boolean,
  isCtaButtonDisabled?: boolean,
  hideCtaButton?: boolean,
}>()
const emit = defineEmits(['hide', 'openRuleModal'])

const { profile } = useAuth()
const { player } = usePlayer()
const { addHapticFeedback } = useHapticFeedback()
const { open } = useTonConnectModal()
const { rawAddress } = useWalletConnect()
const { topNumber } = useFullScreenDimensions()
const slots = useSlots()
const {
  winnersCount,
  prizePool,
  prizeTypeImage,
  prizeTypeText,
  getPrizeForPosition,
  getPrizeNameForPosition,
  getPrizeImageForPosition,
  getPrizeValueForPosition,
  getBadgeForPosition,
  getCardMedal,
} = useChallengeBase(props.challenge)

const loading = ref(false)
const showPrizes = ref(false)
const container = ref<HTMLElement | null>(null)
const inScrollView = ref(false)
const isScrolled = ref(false)
const maxVisibleRank = ref<number>(100)
const listLimit = ref<number>(100)
const shouldAnimateUser = ref(false)

const hasCtaButtonSlot = computed(() => !!slots['ctaButton'])

const playerForCard = computed<CardChallengeLeaderboardPlayer | undefined>(() => {
  if (props.currentPlayer) {
    return {
      firstName: profile.value.firstName,
      photoUrl: profile.value.photoUrl,
      score: props.currentPlayer.score,
      isThisPlayer: true,
    }
  }
  return undefined

})

function handleScroll() {
  if (container.value) {
    if (props.currentPlayer?.rank) {
      const cardElement = container.value.querySelector(`.card:nth-child(${props.currentPlayer.rank})`) as HTMLElement
      if (cardElement) {
        const cardRect = cardElement.getBoundingClientRect()
        inScrollView.value = cardRect.bottom <= container.value.getBoundingClientRect().bottom
      } else {
        inScrollView.value = false
      }
    }
    isScrolled.value = container.value.scrollTop > 30
  }
}

function ctaClick() {
  addHapticFeedback('medium')
  if (!rawAddress.value) {
    open()
    return
  }
  props.onCtaClick?.()
}

function scrollToCurrentUser() {
  if (!props.currentPlayer?.rank || !container.value || props.currentPlayer.rank > maxVisibleRank.value) return

  const userRank = props.currentPlayer.rank
  nextTick(() => {
    const userIndex = userRank - 1
    const cards = container.value?.querySelectorAll('.card')
    const userCardElement = cards?.[userIndex] as HTMLElement

    if (userCardElement) {
      userCardElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

      // Enable animation after a short delay
      setTimeout(() => {
        shouldAnimateUser.value = true

        // Disable animation after 5 seconds
        setTimeout(() => {
          shouldAnimateUser.value = false
        }, 5000)
      }, 500)
    }
  })
}

watch(() => props.show, async () => {
  if (props.show) {
    loading.value = true
    await props.getLeaderboard()
    loading.value = false

    if (props.isReturningFromGame) {
      setTimeout(() => {
        scrollToCurrentUser()
      }, 100)
    }
  } else {
    // Reset animation when modal closes
    shouldAnimateUser.value = false
  }
}, { immediate: true })

watch(rawAddress, async rawAddress => {
  if (!rawAddress) return
  loading.value = true
  await props.getLeaderboard()
  loading.value = false
})

watch([container, props.currentPlayer, props.players], () => {
  inScrollView.value = true
  nextTick(() => {
    setTimeout(() => {
      handleScroll()
    }, 50)
  })
}, { immediate: true })
</script>

<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="fixed max-h-screen h-screen w-screen left-0 bottom-0 bg-dark-most-bottom-layer backdrop-blur-[4px] flex flex-col z-[800] normal-zeros overflow-x-hidden"
    >
      <div
        :class="twMerge('absolute top-0 w-full h-[300px]', backgroundColor)"
        :style="backgroundStyle"
      />
      <div
        ref="container"
        class="h-full w-full max-w-[440px] mx-auto flex-1 flex flex-col px-4 overflow-y-auto overflow-x-hidden"
        :style="{ paddingTop: `${topNumber + 24}px` }"
        @scroll="handleScroll"
      >
        <div class="flex items-center z-[1]">
          <Transition
            name="slide-fade"
            mode="out-in"
          >
            <div
              v-if="showPrizes"
              class="w-6 h-6 min-w-6 flex items-center justify-center top-6 left-6 z-[2] cursor-pointer mr-1.5"
              @click="showPrizes = false"
            >
              <ArrowRight class="w-full h-full rotate-180" />
            </div>
          </Transition>
          <div
            v-if="currencyImage"
            class="flex-1 flex items-center gap-1 mr-4"
          >
            <Transition
              name="pill-slide"
              mode="out-in"
            >
              <div
                :key="showPrizes ? 'with-arrow' : 'without-arrow'"
                class="balance-pill scale-in"
              >
                <div class="w-5 h-5 min-w-[20px] relative flex justify-center">
                  <img
                    class="object-cover w-auto h-5"
                    :src="currencyImage"
                  >
                </div>
                <span>{{ formatNumberFullTruncate((player.specialCurrencies as Record<string, number>)[challengeCurrency!] ?? 0) }}</span>
              </div>
            </Transition>
          </div>
          <CrossCircleIcon
            class="size-8 min-w-[32px] text-white/[87%] ml-auto"
            @click="addHapticFeedback('light'); emit('hide')"
          />
        </div>
        <div class="flex flex-col w-full h-full items-center text-center relative">
          <div class="relative flex justify-center items-center">
            <div :class="twMerge('absolute size-[175px] scale-110 rounded-full blur-[50px] opacity-90', mainImageBackgroundClass)" />
            <img
              :src="mainImage"
              :class="twMerge('relative size-[175px] pointer-events-none z-10', mainImageClass)"
            >
          </div>
          <h2 class="text-light-white text-[36px] leading-9 font-bold mb-2">
            <T
              v-if="showPrizes"
              key-name="collectible_challenge.modal.prizes_title"
              default-value="Prizes"
            />
            <template v-else>
              {{ title }}
            </template>
          </h2>
          <p
            class="font-medium text-base leading-5 -tracking-[0.32px] mb-6 whitespace-pre-line"
            :class="showPrizes ? 'text-light-white' : 'text-light-white/70'"
          >
            <template v-if="showPrizes">
              Prizes will be sent shortly after the end of the challenge. Make sure to
              <span
                :class="{'pointer-events-none': !!rawAddress, 'underline text-teal-primary': !rawAddress}"
                @click="open"
              >
                <T
                  key-name="general.connect"
                  default-value="connect"
                />
              </span>
              your wallet to receive your prize!
            </template>
            <template v-else>
              {{ description }}
            </template>
          </p>

          <Transition
            name="content-fade"
            mode="out-in"
          >
            <div
              v-if="showPrizes && prizePool"
              key="prizes"
              class="w-full flex-1 flex flex-col pb-6"
            >
              <ChallengePrizeItem
                v-for="(prizeItem, ind) in prizePool.amounts"
                :key="ind"
                :prize-type="prizePool.type"
                :prize-item="prizeItem"
                :prize-image="prizeTypeImage"
              />
            </div>

            <div
              v-else
              key="main"
              class="flex-1 w-full flex flex-col items-center"
            >
              <div
                :class="twMerge('mb-6 -mx-6 h-7 w-[calc(100%+48px)] flex items-center justify-center drop-shadow-xl', timerBackgroundClass)"
                :style="timerBackgroundInlineStyle"
              >
                <div class="uppercase text-white text-base font-bold text-shadow-default pr-1.5">
                  Ends in
                </div>
                <img
                  class="size-[18px] object-cover"
                  :src="ClockIcon"
                  alt="Clock"
                >
                <div class="ml-1 golden-text-black-stroke text-base font-bold gold-text-shadow px-0.5">
                  {{ formattedCountdown }}
                </div>
              </div>
              <BaseButton
                v-if="!showPrizes && !hideCtaButton"
                class="mb-6 w-full"
                button-class="border-0 h-full py-4 px-6 h-[60px]"
                :disabled="isCtaButtonDisabled || (isOneTimeChallenge && (currentPlayer?.score ?? 0) > 0)"
                @click="ctaClick"
              >
                <div class="flex items-center gap-x-2">
                  <span v-if="isOneTimeChallenge && currentPlayer?.score">
                    {{ ctaChallengeDoneText }}
                  </span>
                  <T
                    v-else-if="!rawAddress"
                    key-name="wallet.connect_wallet"
                    default-value="Connect wallet"
                  />
                  <template v-else-if="hasCtaButtonSlot">
                    <slot name="ctaButton" />
                  </template>
                  <template v-else>
                    <span>{{ ctaText }}</span>
                    <ArrowRightIcon class="size-6 [&>path]:stroke-2" />
                  </template>
                </div>
              </BaseButton>

              <div class="w-full flex gap-x-4">
                <SummaryCard
                  content-class="pt-3"
                  top-text="Total Prizes"
                  :middle-image="prizeTypeImage"
                  :middle-text="prizeTypeText"
                  :bottom-text="`${winnersCount} WINNERS`"
                  @click="showPrizes = true"
                />
                <SummaryCard
                  background-class="w-1/2"
                  border-class="bg-white/[.38]"
                  top-text="Rule"
                  top-class="text-base font-semibold leading-6 text-light-white"
                  :middle-text="ruleCardLine1"
                  middle-class="text-center text-xl font-bold leading-6 text-indiana-dark"
                  bottom-class="text-center text-xl font-bold leading-6 text-indiana-dark"
                  :has-info-icon="isRuleCardClickable"
                  @click="isRuleCardClickable && emit('openRuleModal')"
                >
                  <template #bottom>
                    <div class="flex">
                      <span class="whitespace-pre-line text-center text-xl font-bold leading-6 text-indiana-dark">
                        {{ ruleCardLine2 }}
                      </span>
                      <img
                        v-if="ruleCardImage"
                        :src="ruleCardImage"
                        class="size-6 object-cover ml-1"
                      >
                    </div>
                  </template>
                </SummaryCard>
              </div>

              <div
                v-if="!loading"
                :class="twMerge('w-full flex flex-col relative', !players.length && 'flex-1 items-center justify-center  h-full')"
              >
                <div
                  v-if="players.length"
                  class="mt-6 flex flex-col w-full gap-y-2 pb-4 items-center"
                >
                  <ChallengeBoardCard2
                    v-for="(leadingBoardPlayer, index) in players.slice(0, maxVisibleRank)"
                    :key="index"
                    :player="leadingBoardPlayer"
                    :index="(index+1).toString()"
                    :is-own="leadingBoardPlayer.isThisPlayer"
                    :medal="getCardMedal(index+1)"
                    :prize-value="getPrizeValueForPosition(index+1)"
                    :prize-image="getPrizeImageForPosition(index+1)"
                    :prize-name="getPrizeNameForPosition(index+1)"
                    :score-image="scoreImage"
                    :score-value="isScoreInMilliseconds ? formatMillisecondDuration(leadingBoardPlayer.score!) : formatNumberSemiTruncate(leadingBoardPlayer.score)"
                    :is-prize-pool-nft="isPrizePoolNft(getPrizeForPosition(index+1))"
                    :badge="getBadgeForPosition(index+1)"
                    class="card"
                  />
                  <p
                    v-if="listLimit <= players.length"
                    class="mt-3 mb-4 text-center max-w-[314px] text-base font-medium text-white/60"
                    :class="currentPlayer && currentPlayer.rank > players.length && 'pb-10'"
                  >
                    And many more players who didn't make it into the top {{ Math.min(players.length, listLimit) }}... yet.
                  </p>
                  <ChallengeBoardCard2
                    v-else-if="players.length > 1"
                    :player="playerForCard"
                    :index="`${(maxVisibleRank).toString()}+`"
                    :prize-value="getPrizeValueForPosition(maxVisibleRank)"
                    :prize-image="getPrizeImageForPosition(maxVisibleRank)"
                    :prize-name="getPrizeNameForPosition(maxVisibleRank+1)"
                    :score-image="scoreImage"
                    :score-value="playerForCard && (isScoreInMilliseconds ? formatMillisecondDuration(playerForCard.score!) : formatNumberSemiTruncate(playerForCard.score))"
                    :is-prize-pool-nft="isPrizePoolNft(getPrizeForPosition(maxVisibleRank+1))"
                    :badge="getBadgeForPosition(maxVisibleRank)"
                    card-class="invisible"
                  />
                </div>
                <div
                  v-else
                  class="flex flex-1 flex-col items-center justify-center w-full text-center py-6"
                >
                  <h2 class="text-lg -tracking-[0.36px] font-bold text-light-white">
                    <T
                      key-name="card-challenge.empty.title"
                      default-value="Nothing to see here yet"
                    />
                  </h2>
                  <p class="text-sm font-medium tracking-tight text-white/[.87]">
                    <T
                      key-name="card-challenge.empty.description"
                      default-value="Be the first to join the challenge"
                    />
                  </p>
                </div>
                <div
                  v-if="playerForCard && currentPlayer && !inScrollView"
                  class="w-full fixed bottom-4 z-[900] left-1/2 -translate-x-1/2 px-4"
                >
                  <ChallengeBoardCard2
                    :player="playerForCard"
                    :index="formatUnshortenedNumber(currentPlayer.rank)"
                    :medal="getCardMedal(currentPlayer.rank)"
                    :prize-value="getPrizeValueForPosition(currentPlayer.rank)"
                    :prize-image="getPrizeImageForPosition(currentPlayer.rank)"
                    :prize-name="getPrizeNameForPosition(currentPlayer.rank)"
                    :score-image="scoreImage"
                    :score-value="playerForCard && (isScoreInMilliseconds ? formatMillisecondDuration(playerForCard.score!) : formatNumberSemiTruncate(playerForCard.score))"
                    :is-prize-pool-nft="isPrizePoolNft(getPrizeForPosition(currentPlayer.rank))"
                    :badge="getBadgeForPosition(currentPlayer.rank)"
                    is-own
                    card-class="border-teal-primary"
                    rank-wrapper-class="min-w-fit mr-1"
                  />
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.balance-pill {
  @apply py-1.5 pl-2.5 pr-3 rounded-full bg-dark-top-most-layer flex items-center gap-x-1.5 h-8 text-sm font-semibold -tracking-[0.28px] min-w-max;
  position: relative;
  transition: all 0.3s ease;
}

.scale-in {
  animation: scaleIn 0.3s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.text-shadow-default {
  text-shadow: 0px 0px 115.874px rgba(0, 0, 0, 0.50);
}
</style>

<style lang="scss">
@import "@/assets/styles/challenges";
</style>